import { useEffect, useState } from "react";
import { Trophy, MapPin, Clock, X, Share2 } from "lucide-react";
import api from "@/lib/api";
import RankCardModal from "@/components/RankCardModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const CATS = ["All", "Companies", "Colleges", "Schools", "Managers", "Visitors"];

export default function Leaderboard() {
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("all");
  const [cat, setCat] = useState("All");
  const [rows, setRows] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [share, setShare] = useState(null);

  useEffect(() => { api.get("/games").then((r) => setGames(r.data)); }, []);
  useEffect(() => {
    const params = {};
    if (gameId !== "all") params.game_id = gameId;
    if (cat !== "All") params.category = cat;
    api.get("/leaderboard", { params }).then((r) => setRows(r.data));
  }, [gameId, cat]);

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);
  const gameName = (id) => games.find((g) => g.id === id)?.name;

  return (
    <div className="max-w-6xl mx-auto px-5 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-amber-400 font-mono text-xs uppercase tracking-wider mb-2">
            <Trophy className="h-4 w-4" /> Global Records · persists across every location
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Leaderboard</h1>
        </div>
        <div className="flex gap-3">
          <Select value={gameId} onValueChange={setGameId}>
            <SelectTrigger data-testid="lb-game-filter" className="w-40 bg-[#131a29] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All games</SelectItem>
              {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="lb-cat-filter" className="w-36 bg-[#131a29] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Podium */}
      {podium.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {podium.map((t, i) => (
            <div key={t.id} data-testid={`podium-${i}`}
              className={`rounded-2xl border p-5 relative overflow-hidden ${i === 0 ? "border-amber-500/50 bg-amber-500/5 sm:-translate-y-2" : "border-white/10 bg-[#131a29]"}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`font-display font-black text-4xl ${i === 0 ? "text-amber-400" : i === 1 ? "text-slate-300" : "text-orange-400"}`}>#{i + 1}</span>
                <span className="font-mono text-emerald-400 text-xl">{fmt(t.completion_seconds)}</span>
              </div>
              {t.photo_url && (
                <button onClick={() => setPhoto(t)} data-testid={`photo-${t.id}`} className="w-full h-32 rounded-xl overflow-hidden mb-3 border border-white/10">
                  <img src={t.photo_url} alt={t.team_name} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                </button>
              )}
              <div className="font-display font-bold text-lg">{t.team_name}</div>
              <div className="text-sm text-slate-400">{t.org_name}</div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-xs text-violet-300 border border-violet-500/30 rounded-full px-2 py-0.5">
                  <MapPin className="h-3 w-3" /> {t.location_name}
                </span>
                <button onClick={() => setShare(t)} data-testid={`share-podium-${t.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-full px-2 py-0.5 transition-colors">
                  <Share2 className="h-3 w-3" /> Share
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-white/10 bg-[#131a29] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Team</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Org</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Location</th>
              <th className="text-right px-4 py-3">Time</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rest.map((t) => (
              <tr key={t.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`lb-row-${t.id}`}>
                <td className="px-4 py-3 font-mono text-slate-500">{t.rank}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {t.photo_url && <img src={t.photo_url} onClick={() => setPhoto(t)} className="h-8 w-8 rounded object-cover cursor-pointer" alt="" />}
                    <span className="font-semibold">{t.team_name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-400 hidden sm:table-cell">{t.org_name}</td>
                <td className="px-4 py-3 text-violet-300 hidden md:table-cell">{t.location_name}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-400">{fmt(t.completion_seconds)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setShare(t)} data-testid={`share-row-${t.id}`}
                    className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200">
                    <Share2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No records yet for this filter.</td></tr>}
          </tbody>
        </table>
      </div>

      {photo && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-6" onClick={() => setPhoto(null)} data-testid="photo-lightbox">
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPhoto(null)} className="absolute -top-10 right-0 text-white/70 hover:text-white"><X /></button>
            <img src={photo.photo_url} alt={photo.team_name} className="w-full rounded-2xl border border-white/10" />
            <div className="mt-3 text-center">
              <div className="font-display font-bold text-xl">{photo.team_name} · {photo.org_name}</div>
              <div className="text-sm text-slate-400 mt-1 inline-flex items-center gap-2">
                <Clock className="h-4 w-4 text-emerald-400" /> {fmt(photo.completion_seconds)}
                <MapPin className="h-4 w-4 text-violet-400 ml-2" /> {photo.location_name}
              </div>
              {(photo.designation || photo.department || photo.reports_to || photo.year || photo.members) && (
                <div className="mt-3 text-xs text-slate-400 space-y-0.5 text-left inline-block" data-testid="lightbox-details">
                  {photo.designation && <div>Role: <span className="text-slate-200">{photo.designation}</span></div>}
                  {photo.department && <div>Department: <span className="text-slate-200">{photo.department}</span></div>}
                  {photo.reports_to && <div>Reports to: <span className="text-slate-200">{photo.reports_to}</span></div>}
                  {photo.year && <div>Year / Class: <span className="text-slate-200">{photo.year}</span></div>}
                  {photo.members && <div>Members: <span className="text-slate-200">{photo.members}</span></div>}
                </div>
              )}
              <div className="mt-4">
                <button onClick={() => { setShare(photo); setPhoto(null); }} data-testid="share-lightbox"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition-all">
                  <Share2 className="h-4 w-4" /> Share on WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {share && <RankCardModal entry={share} gameName={gameName(share.game_id)} onClose={() => setShare(null)} />}
    </div>
  );
}
